<template>
  <div class="how-to-play">
    <h2 class="section-title large">How to Play</h2>
    <p class="subtitle">With coverage of 160+ Lucky Numbers games, we will show you how to quickly start playing so that you don't miss out.</p>

    <div class="sections">
      <!-- Step 1: Select Your Game -->
      <section class="instruction-section">
        <div class="section-content image-first">
          <div class="image-content">
            <img :src="howToPlayStep1" alt="Select Your Game" />
          </div>
          
          <div class="text-content">
            <h3>SELECT YOUR GAME</h3>
            <p>Browse our extensive selection of Lucky Numbers games from around the world. Click the "BET" button on any game card to view available betting markets and start your wager.</p>
            <p>You can explore upcoming draws on the main page or use the search and filter options to find your preferred game.</p>
            <p>Each game features unique draw times and betting opportunities, giving you multiple chances to win throughout the day.</p>
          </div>
        </div>
      </section>

      <!-- Step 2: Select Draws -->
      <section class="instruction-section">
        <div class="section-content image-last">
          <div class="text-content">
            <h3>SELECT YOUR DRAWS</h3>
            <p>After choosing your game, you'll see a list of upcoming draws available for betting. Select one or more draws by checking the boxes next to each draw date and time.</p>
            <p>The available draws are limited to draws within a week period. For Rapid Lucky Numbers, a maximum of 10 draws will be available due to their frequent occurrence.</p>
            <p>You can select multiple draws to increase your winning potential while placing a single wager.</p>
          </div>
          <div class="image-content">
            <img :src="howToPlayStep2" alt="Select Your Draws" />
          </div>
        </div>
      </section>

      <!-- Step 3: Select Market -->
      <section class="instruction-section">
        <div class="section-content image-first">
          <div class="image-content">
            <img :src="howToPlayStep3" alt="Select Your Market" />
          </div>
          <div class="text-content">
            <h3>SELECT YOUR MARKET</h3>
            <p>Choose from a variety of betting markets based on the game type. Options include selecting specific numbers, betting on sum totals (odd/even, over/under), or predicting bonus ball outcomes.</p>
            <p>For standard bet markets, select your numbers from the available number pool. For sum-based markets, simply choose your prediction (odd/even or over/under the boundary).</p>
            <p>Once you've made your selection, the wager will be added to your betslip automatically.</p>
          </div>
        </div>
      </section>

      <!-- Step 4: Confirm Your Entries -->
      <section class="instruction-section">
        <div class="section-content image-last">
          <div class="text-content">
            <h3>CONFIRM YOUR ENTRIES</h3>
            <p>Review your selections in the confirmation section. You can add multiple lines to your wager or modify your selections before finalizing.</p>
            <p>Adjust your stake per entry if desired. The system will display the total possible winnings for each line based on the odds and your stake amount.</p>
            <p>Verify all your selections, stake amounts, and potential payouts before proceeding to the betslip.</p>
          </div>
          <div class="image-content">
            <img :src="howToPlayStep4" alt="Confirm Your Entries" />
          </div>
        </div>
      </section>

      <!-- Step 5: Your Betslip -->
      <section class="instruction-section">
        <div class="section-content image-first">
          <div class="image-content">
            <img :src="howToPlayStep5" alt="Your Betslip" />
          </div>
          <div class="text-content">
            <h3>YOUR BETSLIP</h3>
            <p>Your betslip displays all active wagers ready for placement. Review each entry including the selected draws, market type, numbers or predictions, stake, and potential winnings.</p>
            <p>You can modify individual stakes, remove any unwanted entries, or add more bets from other games before placing your final wager.</p>
            <p>Once you're satisfied with your selections, click the "PLACE BET" button to submit your wagers. All bets must be confirmed before the draw cutoff time.</p>
          </div>
        </div>
      </section>

      <!-- Step 6: Wager Details -->
      <section class="instruction-section">
        <div class="section-content image-last">
          <div class="text-content">
            <h3>WAGER DETAILS</h3>
            <p>After placing your bet, navigate to the "Wagers" page to view all your betting history. Each transaction shows the date, description, status, total stake, and payout information.</p>
            <p>Access detailed information for each wager including transaction date, game details, draw information, market type, your selections, results, odds, and payout amount.</p>
            <p>Winnings are automatically credited to your account wallet once the draw results are published and your bet is successful.</p>
          </div>
          <div class="image-content">
            <img :src="howToPlayStep6" alt="Wager Details" />
          </div>
        </div>
      </section>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { defineOptions } from 'vue'
import { useTheme } from '@/composables/useTheme'

defineOptions({
  name: 'HowToPlayView'
})

const { getAssetPath } = useTheme()

const howToPlayStep1 = computed(() => getAssetPath('howToPlay', 'step1') || '/img/images/how-to-play/1-select-game.png')
const howToPlayStep2 = computed(() => getAssetPath('howToPlay', 'step2') || '/img/images/how-to-play/2-select-draws.png')
const howToPlayStep3 = computed(() => getAssetPath('howToPlay', 'step3') || '/img/images/how-to-play/3-select-market.png')
const howToPlayStep4 = computed(() => getAssetPath('howToPlay', 'step4') || '/img/images/how-to-play/4-confirm-your-entries.png')
const howToPlayStep5 = computed(() => getAssetPath('howToPlay', 'step5') || '/img/images/how-to-play/5-your-betslip.png')
const howToPlayStep6 = computed(() => getAssetPath('howToPlay', 'step6') || '/img/images/how-to-play/6-wager-details.png')
</script>

<style scoped>
.how-to-play {
  max-width: 100%;
  margin: 0 auto;
}

.sections {
  display: flex;
  flex-direction: column;
}

.instruction-section {
  background: var(--bg-instruction-section, rgba(16, 21, 32, 0.05));
  padding: 0px !important;
}

.instruction-section h3 {
  color: var(--text-primary);
  font-size: 24px;
  font-weight: 600;
  margin-bottom: 24px;
}

.section-content {
  display: grid;
  gap: 22px;
  align-items: start;
}

.section-content.image-first {
  grid-template-columns: 30% 70%;
}

.section-content.image-last {
  grid-template-columns: 70% 30%;
}

.text-content {
  color: var(--text-secondary);
  text-align: left;
  padding: 20px;
}

.text-content p {
  margin-bottom: 16px;
  line-height: 1.6;
}

.text-content p:last-child {
  margin-bottom: 0;
}

.image-content {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  padding: 20px;
  background: var(--bg-instruction-image, none);
}

.image-content img {
  width: 100%;
  max-width: 260px;
  height: auto;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
  display: block;
  margin: 0;
}

@media (min-width: 769px) {
  .image-content {
    min-height: 180px;
  }
}

@media (max-width: 768px) {
  .section-content {
    display: flex;
    flex-direction: column;
    gap: 0;
  }

  .image-content {
    order: 0 !important;
    margin: 0 auto 16px auto;
    max-width: 100%;
    display: flex;
    justify-content: center;
    width: 100%;
  }

  .image-content img {
    width: 100%;
    max-width: 260px;
    height: auto;
    display: block;
    margin: 0 auto;
  }

  .instruction-section {
    padding: 20px 12px;
  }

  .instruction-section h3 {
    color: var(--text-primary);
    font-size: 18px;
    font-weight: 700;
    margin-bottom: 18px;
    text-align: left;
    margin-top: 0;
  }

  .text-content {
    text-align: left;
    font-size: 15px;
    color: var(--text-primary);
    margin-top: 0;
  }

  .text-content p {
    margin-bottom: 12px;
    line-height: 1.6;
  }

  .text-content p:last-child {
    margin-bottom: 0;
  }

  .section-content.image-last .image-content {
    order: 0;
  }
  .section-content.image-last .text-content {
    order: 1;
  }
}
</style>